import React, { useCallback, useEffect, useState } from "react";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import { Badge } from "components/common/Badge";
import { Button } from "components/common/Button";
import { Skeleton } from "components/common/Skeleton";
import { Inline } from "components/common/Stack";
import { TooltipButton } from "components/common/TooltipButton";
import { formatShortDate } from "@/lib/dates";
import { formatProcessorMoney } from "@/lib/format";
import {
  fetchInvoicePdfUrl,
  fetchInvoices,
  type InvoiceView,
} from "@/lib/api/organizations";

const SETTLED_INVOICE_STATUSES = new Set(["completed", "paid"]);

export const InvoiceHistory: React.FC<{ orgId: string; refresh?: number }> = ({
  orgId,
  refresh,
}) => {
  const [invoices, setInvoices] = useState<InvoiceView[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  // A list that failed to load is not an empty one.
  const [read, setRead] = useState<"loading" | "loaded" | "failed">("loading");
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    async (stale?: { cancelled: boolean }) => {
      setRead("loading");
      const page = await fetchInvoices(orgId);
      if (stale?.cancelled) return;
      if (!page) {
        setRead("failed");
        return;
      }
      setInvoices(page.invoices);
      setHasMore(page.has_more);
      setCursor(page.next_cursor);
      setRead("loaded");
    },
    [orgId],
  );

  useEffect(() => {
    const stale = { cancelled: false };
    setInvoices([]);
    void load(stale);
    return () => {
      stale.cancelled = true;
    };
  }, [load, refresh]);

  const loadMore = useCallback(async () => {
    if (!cursor) return;
    const page = await fetchInvoices(orgId, cursor);
    if (!page) return;
    setInvoices((prev) => [...prev, ...page.invoices]);
    setHasMore(page.has_more);
    setCursor(page.next_cursor);
  }, [cursor, orgId]);

  /// The URL expires, so it is fetched on the click.
  const download = useCallback(
    async (transactionId: string) => {
      setBusy(transactionId);
      setError(null);
      const url = await fetchInvoicePdfUrl(transactionId, orgId);
      setBusy(null);
      if (!url) {
        setError("That invoice could not be opened. Please try again.");
        return;
      }
      window.open(url, "_blank", "noopener,noreferrer");
    },
    [orgId],
  );

  // Skeleton at the ledger's own row height so the section does not resize.
  if (read === "loading") {
    return (
      <>
        {[0, 1, 2].map((row) => (
          <div key={row} className="flex h-9 items-center gap-3 px-2">
            <span className="w-24 shrink-0">
              <Skeleton width="100%" />
            </span>
            <span className="w-20 shrink-0">
              <Skeleton width="100%" />
            </span>
            <span className="min-w-0 flex-1">
              <Skeleton width="40%" />
            </span>
          </div>
        ))}
      </>
    );
  }

  if (read === "failed") {
    return (
      <Inline gap="sm" align="center" className="px-2 py-2">
        <span className="text-fg-muted-regular-small">
          Invoices couldn&rsquo;t be loaded.
        </span>
        <Button variant="quiet-link" size="sm" onClick={() => void load()}>
          Try again
        </Button>
      </Inline>
    );
  }

  if (invoices.length === 0) {
    return (
      <span className="px-2 py-2 text-fg-muted-regular-small">
        No invoices yet. One appears here after each payment.
      </span>
    );
  }

  return (
    <>
      {invoices.map((invoice) => (
        <div
          key={invoice.transaction_id}
          className="flex items-center gap-3 px-2 py-2"
        >
          <span className="text-fg-muted-regular w-24 shrink-0 tabular-nums">
            {invoice.billed_at
              ? formatShortDate(new Date(invoice.billed_at * 1000))
              : "—"}
          </span>
          <span className="text-figure w-20 shrink-0 text-right">
            {formatProcessorMoney(invoice.total, invoice.currency_code)}
          </span>
          <span className="text-mono-small-muted min-w-0 flex-1 truncate">
            {invoice.invoice_number ?? ""}
          </span>
          {invoice.refunded && (
            <span
              className="text-fg-muted-regular-small shrink-0 tabular-nums"
              data-testid="invoice-refunded"
            >
              {`−${formatProcessorMoney(invoice.refunded, invoice.currency_code)} refunded`}
            </span>
          )}
          {!SETTLED_INVOICE_STATUSES.has(invoice.status) && (
            <Badge
              label={invoice.status.replace(/_/g, " ")}
              variant={invoice.status === "past_due" ? "error" : "default"}
              className="shrink-0"
            />
          )}
          <span className="shrink-0">
            <TooltipButton
              title={
                invoice.invoice_number
                  ? `Download invoice ${invoice.invoice_number} (PDF)`
                  : "Download the invoice PDF"
              }
              variant="default"
              size="xs"
              loading={busy === invoice.transaction_id}
              onClick={() => void download(invoice.transaction_id)}
              aria-label={
                invoice.invoice_number
                  ? `Download invoice ${invoice.invoice_number}`
                  : "Download invoice"
              }
            >
              <DownloadOutlinedIcon sx={{ fontSize: 14 }} />
            </TooltipButton>
          </span>
        </div>
      ))}
      {error && <span className="px-2 py-2 text-error-regular">{error}</span>}
      {hasMore && (
        <span className="px-2 py-2">
          <Button
            variant="quiet-link"
            size="sm"
            onClick={() => void loadMore()}
          >
            Load more
          </Button>
        </span>
      )}
    </>
  );
};
